import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';

export interface TooltipProps {
    id: string | React.ReactElement;
    content: string;
    index?: string;
    elementId?: string;
    href?: string;
    replacement?: any;
    place?: 'left' | 'top' | 'right' | 'bottom';
    type?: 'dark' | 'success' | 'warning' | 'error' | 'info' | 'light';
    effect: 'solid' | 'float';
    icon?: any;
    empty?: boolean;
    triggerClassName?: string;
    contentClassName?: string;
    triggerDecorationClassName?: string;
    dataAutomationId?: string;
    plain?: boolean;
    multiline?: boolean;
    contentReplacement?: any;
    onClickHandler?: (e: any) => void;
    isIdDynamicText?: boolean;
    linkDomId?: string;
};

/**
 * id - trigger id (used for Formatted message to render trigger text). It is not used for tooltip where trigger is icon;
 * content - content id for Formatted Message or whole html which will be rendered;
 * index - unique identifier to construct unique data-for attributes;
 * replacement - object for trigger value;
 * icon - If this param is passed, tooltip's trigger is not text, but an icon;
 * triggerClassName - custom class names for trigger;
 * contentClassName - custom class names for content;
 * triggerDecorationClassName - by default all tooltips have dotted bottom border;
 * dataAutomationId - optional prop needed for automation QA.
 * plain - when content is a dynamic value, which does not need to be parsed(translated)
 *
 * @param props
 * @returns {*}
 * @constructor
 */
const Tooltip = (props: TooltipProps) => {
    const {
        id,
        elementId=undefined,
        content,
        index,
        href,
        replacement,
        place='top',
        type='dark',
        effect='solid',
        icon=undefined,
        empty=false,
        triggerClassName='',
        contentClassName='',
        triggerDecorationClassName='tooltip-base',
        dataAutomationId=undefined,
        plain=false,
        multiline=false,
        contentReplacement,
        onClickHandler,
        isIdDynamicText=false,
        linkDomId = null,
    } = props;

    const intlApi = useIntl();

    let tRef: any = null;

    const onKeyUpEvent = (e, tooltipRef) => {
        // check for Esc key
        if (e.keyCode === 27) {
            hideTooltip(e, tooltipRef);
        }
    };

    const hideTooltip = (e, tooltipRef) => {
        ReactTooltip.hide(tooltipRef);
    };

    const showTooltip = (e, tooltipRef) => {
        ReactTooltip.show(tooltipRef);
    };

    const gtmHoverEvent = () => {
        // @ts-ignore
        window.dataLayer.push({ 'event': 'hoverEvent', dataAutomationId, elementId, content });
    };

    const uniqueIdentifier = () => icon ? `tooltip_${index}` : elementId ? `${elementId}_tooltip_${index}` : `${id}_tooltip_${index}`;

    const defineLinkPropsIfLink = () => href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {};

    const getTooltipTriggerType = () => {
        if (icon) {
            return <FontAwesomeIcon icon={icon}/>; // icon, not text;
        } else if (plain || React.isValidElement(id) || isIdDynamicText) {
            return id; // dynamic text, no need to parse/translate;
        } else if (!empty && typeof id === 'string') {
            return <FormattedMessage id={id} values={replacement}/>; // i18n id, will be translated;
        }
        return null;
    };

    const tooltipTriggerType =  getTooltipTriggerType();

    return <span className='ga-tooltip-container' onMouseOver = { gtmHoverEvent }>
        <a data-tip
            ref={ref => tRef = ref }
            data-for={uniqueIdentifier()}
            { ...defineLinkPropsIfLink() }
            {...(dataAutomationId ? {'data-automation-id': `${dataAutomationId}_tooltip_base`} : {}) }
            className={`text-base cursor-pointer ${triggerClassName} ${triggerDecorationClassName}`}
            onClick={onClickHandler}
            {...(linkDomId ? {id: linkDomId} : {}) }
        >
            {tooltipTriggerType}
        </a>
        <span tabIndex={0}
            className={`gi-tooltip ${contentClassName}`}
            {...(dataAutomationId ? {'data-automation-id': `${dataAutomationId}_tooltip_content`} : {}) }
            onFocus={(e) => showTooltip(e, tRef)}
            onBlur={(e) => hideTooltip(e, tRef)}
            onKeyUp={(e) => onKeyUpEvent(e, tRef)}>
            <ReactTooltip
                place={place}
                type={type}
                effect={effect}
                className='tooltip-component'
                id={uniqueIdentifier()}
                multiline={multiline}
            >
                { typeof content === 'string' && !plain ? parse(`${intlApi.formatMessage({ id: content }, contentReplacement)}`) : content }
            </ReactTooltip>
        </span>
    </span>;
};

Tooltip.propTypes = {
    id: PropTypes.any.isRequired,
    content: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    index: PropTypes.string,
    elementId: PropTypes.string,
    href: PropTypes.string,
    replacement: PropTypes.any,
    place: PropTypes.oneOf(['left', 'top', 'right', 'bottom']),
    type: PropTypes.oneOf(['dark', 'success', 'warning', 'error', 'info', 'light']),
    effect: PropTypes.oneOf(['solid', 'float']),
    icon: PropTypes.any,
    empty: PropTypes.bool,
    triggerClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    triggerDecorationClassName: PropTypes.string,
    dataAutomationId: PropTypes.string,
    plain: PropTypes.bool,
    multiline: PropTypes.bool,
    contentReplacement: PropTypes.any,
    onClickHandler: PropTypes.func
};

Tooltip.defaultProps = {
    elementId: undefined,
    place: 'top',
    type: 'dark',
    effect: 'solid',
    icon: undefined,
    empty: false,
    triggerClassName: '',
    contentClassName: '',
    triggerDecorationClassName: 'tooltip-base',
    dataAutomationId: undefined,
    plain: false,
    multiline: false,
};
export default Tooltip;